/**
 * The intelligence report screen.
 *
 * For the chief executive, and for whatever they put it in front of: a board,
 * GACA, an insurer. Each finding is one row with three columns — what will
 * break and when, what it costs, who holds it — and a defensibility line
 * underneath that says how far the row can be relied on and why. The
 * appendix lists every record cited, so a reader with platform access can
 * open each one.
 *
 * The screen gathers the inputs from the platform in one query and hands
 * them to `buildIntelligenceReport`, which is pure and tested on its own. A
 * source that is missing — a register the platform does not have, a model
 * never fitted — is recorded as a gap in the report rather than failing the
 * whole page: the reader is told what is absent.
 */
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { money, type Evidence, type Finding, type IntelligenceReport } from "../../domain/intelligence";
import { Button, Card, ErrorState, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { useIntelligence } from "./gather";
import s from "./Intelligence.module.css";
import { t, tn } from "../../i18n";

export function IntelligenceReportPage() {
  const session = useSession();
  const gathered = useIntelligence();
  const report = { ...gathered, data: gathered.data?.report };
  return (
    <Page
      eyebrow={`${session.site.code} · ${t("Executive")}`}
      title="Intelligence report"
      actions={
        <>
          {report.data && (
            <span className={s.stamp}>
              {t("generated {date} UTC", { date: report.data.generatedAt.replace("T", " ").slice(0, 16) })} ·{" "}
              {report.data.siteCode}
            </span>
          )}
          <Button variant="primary" onClick={() => window.print()} disabled={!report.data}>
            {t("Print for the board")}
          </Button>
        </>
      }
    >
      {report.isPending && <Skeleton rows={8} />}
      {gathered.isError && <ErrorState error={gathered.error} retry={() => void gathered.refetch()} />}
      {report.data && <Report r={report.data} />}
    </Page>
  );
}

function Report({ r }: { r: IntelligenceReport }) {
  const ahead = r.findings.filter((f) => f.phase === "ahead");
  const now = r.findings.filter((f) => f.phase === "now");
  return (
    <div className={`${s.report} intelligence-report`}>
      <section className={s.tiles} aria-label={t("Summary")}>
        <Tile
          n={r.summary.ahead}
          label={t("going to break")}
          sub={t("{n} inside 30 days", { n: r.summary.within30Days })}
        />
        <Tile n={r.summary.now} label={t("broken, still open")} sub={t("already failing or overdue")} />
        <Tile
          n={money(r.summary.exposure.amount, r.summary.exposure.currency)}
          label={t("priced exposure")}
          sub={t("{priced} priced · {unpriced} the platform will not price", {
            priced: r.summary.exposure.priced,
            unpriced: r.summary.exposure.unpriced,
          })}
        />
        <Tile
          n={r.summary.attributable}
          label={t("with a named party")}
          sub={t("{n} nobody is on record for", { n: r.findings.length - r.summary.attributable })}
        />
        <Tile
          n={`${r.summary.byGrade.strong} / ${r.summary.byGrade.partial} / ${r.summary.byGrade.weak}`}
          label={t("strong / partial / weak")}
          sub={t("how far each finding can be defended")}
        />
      </section>

      <p className={s.lead}>
        {t(
          "Each row shows what will break, what it costs and who holds it, with the evidence. Anything the platform can't price or assign is marked as such.",
        )}
      </p>

      <Section
        title="What is going to break"
        sub="soonest first"
        findings={ahead}
        empty="Nothing is expected to fail by the current rules and models. See the method note."
      />
      <Section
        title="Broken and still open"
        sub="worst first"
        findings={now}
        empty="Nothing open that would stop the airport, and no work past its due date."
      />

      {r.gaps.length > 0 && (
        <Card
          title="What the platform could not supply"
          sub="Missing inputs this report doesn't cover"
        >
          <ul className={s.plain}>
            {r.gaps.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </Card>
      )}

      <Card
        title="Evidence appendix"
        sub={tn(
          r.appendix.length,
          "{n} record cited — each opens on the platform",
          "{n} records cited — each opens on the platform",
        )}
      >
        <table className={s.appendix}>
          <thead>
            <tr>
              <th>{t("Kind")}</th>
              <th>{t("Reference")}</th>
              <th>{t("Record")}</th>
              <th>{t("Detail")}</th>
            </tr>
          </thead>
          <tbody>
            {/* two alerts on one asset share kind and reference; the position keeps them apart */}
            {r.appendix.map((e, i) => (
              <tr key={`${e.kind}:${e.ref}:${i}`}>
                <td className="mono">{e.kind.replace("_", " ")}</td>
                <td className="mono">{e.href ? <Link to={e.href}>{e.ref}</Link> : e.ref}</td>
                <td>{e.label}</td>
                <td className={s.detail}>{e.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="Method" sub="How each column is worked out">
        <div className={s.method}>
          <p>
            <b>{t("Prediction.")}</b>{" "}
            {t(
              "Predictive alerts come from the platform's rules on its own readings, showing the rule, its settings and the values that triggered it. Wear-out findings come from failure records across a whole group of units, since unit ages aren't recorded. Overdue tests, obligations and work use the platform's own dates.",
            )}
          </p>
          <p>
            <b>{t("Cost.")}</b>{" "}
            {t(
              "From the deferred-cost register when it prices the same asset or zone; otherwise, the planned cost on the work order. Nothing without a rate is priced.",
            )}
          </p>
          <p>
            <b>{t("Responsibility.")}</b>{" "}
            {t(
              "The crew or contractor on the alert's work order, or on open work for the same asset or zone. For records that disagree, the later party; for a failed hold point, the project. If no work is assigned, no one is named, and that counts as a weakness.",
            )}
          </p>
          <p>
            <b>{t("Defensibility.")}</b>{" "}
            {t(
              "Strong: dated platform records plus a verified rule or a conclusive model check. Partial: something is missing, such as a cost, a named party, an agreed threshold or a conclusive check. Weak: the threshold is unverified, the model has drifted, or the record is a demo. Regulatory references come from the rule itself.",
            )}
          </p>
        </div>
      </Card>
    </div>
  );
}

function Tile({ n, label, sub }: { n: number | string; label: string; sub: string }) {
  return (
    <div className={s.tile}>
      <div className={s.tileN}>{n}</div>
      <div className={s.tileLabel}>{label}</div>
      <div className={s.tileSub}>{sub}</div>
    </div>
  );
}

function Section({ title, sub, findings, empty }: { title: string; sub: string; findings: Finding[]; empty: string }) {
  return (
    <Card title={title} sub={`${findings.length} — ${t(sub)}`}>
      {findings.length === 0 ? (
        <p className={s.empty}>{t(empty)}</p>
      ) : (
        <div className={s.findings}>
          {findings.map((f) => (
            <FindingRow key={f.id} f={f} />
          ))}
        </div>
      )}
    </Card>
  );
}

function FindingRow({ f }: { f: Finding }) {
  return (
    <article
      className={`${s.finding} ${s[`sev${Math.min(5, Math.max(1, f.severity))}`]}`}
      data-testid={`finding-${f.id}`}
    >
      <header className={s.findingHead}>
        <span className={s.sev} title={t("severity {n}", { n: f.severity })}>
          S{f.severity}
        </span>
        <h3 className={s.headline}>{f.headline}</h3>
        <span className={s.when}>{f.horizon.text}</span>
        {f.where && <span className={s.where}>{f.where}</span>}
      </header>
      <div className={s.columns}>
        <Col title="What will break">
          <p>{f.prediction.statement}</p>
          {f.prediction.source && <p className={s.source}>{f.prediction.source}</p>}
          <Refs list={f.prediction.evidence} />
        </Col>
        <Col title="What it costs">
          <p className={f.cost.amount != null ? s.money : undefined}>{f.cost.statement}</p>
          <Refs list={f.cost.evidence} />
        </Col>
        <Col title="Who holds it">
          <p>
            {f.attribution.party ? (
              <b className={s.party}>{f.attribution.party}</b>
            ) : (
              <b className={s.nobody}>{t("Nobody named")}</b>
            )}
            {" — "}
            {f.attribution.statement}
          </p>
          <Refs list={f.attribution.evidence} />
        </Col>
      </div>
      <footer className={`${s.defence} ${s[f.defensibility.grade]}`}>
        <span className={s.grade}>{t(f.defensibility.grade)}</span>
        {f.defensibility.reasons.map((r) => (
          <span key={r} className={s.reason}>
            ✓ {r}
          </span>
        ))}
        {f.defensibility.caveats.map((c) => (
          <span key={c} className={s.caveat}>
            ! {c}
          </span>
        ))}
        {f.defensibility.regulatory.map((r) => (
          <span key={r} className={s.reg}>
            § {r}
          </span>
        ))}
      </footer>
    </article>
  );
}

function Col({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={s.col}>
      <div className={s.colTitle}>{t(title)}</div>
      {children}
    </div>
  );
}

function Refs({ list }: { list: Evidence[] }) {
  if (list.length === 0) return null;
  return (
    <div className={s.refs}>
      {list.map((e, i) => (
        <span key={`${e.kind}:${e.ref}:${i}`} className={s.ref} title={e.detail}>
          {e.href ? <Link to={e.href}>{e.ref}</Link> : e.ref}
        </span>
      ))}
    </div>
  );
}
